import { QuickFilterScopeType, type QuickFilter } from "@amzn/ads-blink-client";
import {
    Button,
    Flex,
    IconButton,
    SplitButton,
    TextButton,
    Text,
    Tooltip,
    Icon,
} from "@amzn/storm-ui";
import { globe, pencil } from "@amzn/storm-ui-icons";
import { useMountEffect } from "@react-hookz/web";
import type { ReactElement } from "react";
import React, { useState, useEffect, useMemo } from "react";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../../../constants/telemetry";
import useEncodedUnifiedFilterStateUrl from "../../../../hooks/misc/useEncodedUnifiedFilterStateUrl";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import { unifiedFilterStore } from "../../../../store/unifiedFilters";
import type { PartialUnifiedfilterState } from "../../../../utils/filters.utils";
import { getProcessedInitialUnifiedFilterState } from "../../../../utils/filters.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";
import { StyledSplitButtonContent } from "../discoveryFilterSharedStyles";

import { QuickFilterManagementModal } from "./QuickFilterManagementModal";
import { SearchQuickFilterActionAsync } from "./useSearchQuickFilterAction";

export const QuickFilterDropdown = (): ReactElement => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [quickFilterUnderManagement, setQuickFilterUnderManagement] =
        useState<QuickFilter>();
    const [quickFilters, setQuickFilters] = useState<QuickFilter[]>([]);

    const sortedQuickFilters = useMemo(() => {
        return [...quickFilters].sort(
            (a, b) =>
                (b.scopeType === QuickFilterScopeType.GLOBAL ? 1 : 0) -
                (a.scopeType === QuickFilterScopeType.GLOBAL ? 1 : 0),
        );
    }, [quickFilters]);

    const [_, searchQuickFiltersAction] = SearchQuickFilterActionAsync();

    const [setEncodedUnifiedFilterStateUrl] = useEncodedUnifiedFilterStateUrl();
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    // Track unified filter store states needed for clearing current filter
    const [
        isContentDirty,
        isMetadataDirty,
        isLastSearchParametersDifferent,
        currentQuickFilterId,
        setCurrentQuickFilterId,
    ] = useStore(unifiedFilterStore, (state) => [
        state.isContentDirty,
        state.isMetadataDirty,
        state.isLastSearchParametersDifferent,
        state.currentQuickFilterId,
        state.setCurrentQuickFilterId,
    ]);

    // Clear current filter selection when filters change, but not during loading
    const shouldClearFilter = useMemo(
        () =>
            isLastSearchParametersDifferent() &&
            currentQuickFilterId &&
            !isContentDirty &&
            !isMetadataDirty,
        [
            isLastSearchParametersDifferent,
            currentQuickFilterId,
            isContentDirty,
            isMetadataDirty,
        ],
    );

    useEffect(() => {
        if (shouldClearFilter) {
            setCurrentQuickFilterId(undefined);
        }
    }, [shouldClearFilter, setCurrentQuickFilterId]);

    // On component load, retrieve our quick filters
    useMountEffect(async () => {
        void fetchQuickFilters();
    });

    const openManagementModal = (quickFilter?: QuickFilter) => {
        setQuickFilterUnderManagement(quickFilter);
        setIsModalOpen(true);
    };

    /**
     * Retrieves the user's quick filters for render
     */
    const fetchQuickFilters = async () => {
        try {
            const response = await searchQuickFiltersAction.execute();
            setQuickFilters(response.data ?? []);
        } catch (error) {
            console.error("Error fetching quick filters", error);
        }
    };

    /**
     * Performs the logic needed to load the filter into the filter store
     */
    const loadFilter = (filter: QuickFilter) => {
        let parsed: PartialUnifiedfilterState | undefined;
        try {
            parsed = filter.filter ? JSON.parse(filter.filter) : undefined;
        } catch (error) {
            console.error("Error parsing quick filter", error);
            upsertNotification(
                "Failed to parse filter when loading. Please try again, or contact support.",
                NotificationType.ERROR,
            );
            return;
        }
        const state = getProcessedInitialUnifiedFilterState(parsed);
        if (state) {
            setEncodedUnifiedFilterStateUrl(state);
            setCurrentQuickFilterId(filter.identifier);
        }

        logWithTakt(TELEMETRY_ID.QUICK_FILTER.APPLY, {
            id: filter.identifier,
        });
    };

    /**
     * Opens the save filter modal
     */
    const saveFilter = () => {
        openManagementModal();
    };

    const editFilter = (e: React.MouseEvent, filter: QuickFilter) => {
        e.stopPropagation();
        openManagementModal(filter);
    };

    return (
        <>
            <QuickFilterManagementModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                handleSubmit={() => void fetchQuickFilters()}
                quickFilter={quickFilterUnderManagement}
                quickFilters={quickFilters}
            />

            <SplitButton
                data-testid="quick-filter-dropdown-button"
                mainButtonLabel="Save Filters"
                mainButtonOnClick={saveFilter}
            >
                {quickFilters.length === 0 && (
                    <div style={{ maxWidth: 200, textAlign: "center" }}>
                        <Text withMargin>
                            Your saved quick filters will show here.
                        </Text>
                        <Text>
                            Click{" "}
                            <TextButton
                                onClick={saveFilter}
                                data-testid="save-quickfilter-when-empty"
                            >
                                here
                            </TextButton>
                            , or the save button above, to save your first one!
                        </Text>
                    </div>
                )}
                <StyledSplitButtonContent>
                    {sortedQuickFilters.map((filter) => {
                        const isCurrentFilter =
                            currentQuickFilterId === filter.identifier;

                        return (
                            <Flex
                                key={filter.identifier}
                                justifyContent="space-between"
                                alignItems="center"
                                columnGap="xxxsmall"
                            >
                                <Button
                                    data-testid={`quick-filter-${filter.identifier}`}
                                    variant="transparent"
                                    small={true}
                                    style={{
                                        flexGrow: 1,
                                        justifyContent: "flex-start",
                                        fontWeight: isCurrentFilter
                                            ? "bold"
                                            : "normal",
                                    }}
                                    onClick={() => loadFilter(filter)}
                                >
                                    {filter.scopeType ===
                                        QuickFilterScopeType.GLOBAL && (
                                        <Tooltip
                                            trigger={
                                                <Icon
                                                    data-testid="quick-filter-global-icon"
                                                    type={globe}
                                                    style={{
                                                        color: "grey",
                                                        verticalAlign: "middle",
                                                    }}
                                                    size="small"
                                                />
                                            }
                                            transitionDuration={{
                                                exit: 0,
                                            }}
                                            position="left"
                                            message="Available in any account"
                                            nonInteractive={true}
                                        />
                                    )}
                                    {filter.name}
                                </Button>

                                <IconButton
                                    icon={pencil}
                                    label={"Edit Quick Filter"}
                                    variant="transparent"
                                    onClick={(e) => editFilter(e, filter)}
                                    data-testid={`edit-quick-filter-${filter.identifier}`}
                                />
                            </Flex>
                        );
                    })}
                </StyledSplitButtonContent>
            </SplitButton>
        </>
    );
};
